/**
 * SKY CLOCK CONTROLLER — maps game state + wall time → continuous hour for the renderer.
 * No canvas imports. See docs/sky-clock.md.
 */

import { SKY_BACKGROUND_MODES } from "../config/constants.js";
import { clamp01, CYCLE_HOURS, easeInOut } from "./sky-clock.js";

export const SKY_MODE = {
  FALL: "fall",
  TRANSITION_TO_SHAKE: "transition_to_shake",
  SHAKE_CRAWL: "shake_crawl",
  TRANSITION_TO_FALL: "transition_to_fall",
};

var FALL_END_HOUR = 12;
var SHAKE_CRAWL_START_HOUR = 16;
var SHAKE_CRAWL_TARGET_HOUR = 21;
var SHAKE_CRAWL_SPAN = SHAKE_CRAWL_TARGET_HOUR - SHAKE_CRAWL_START_HOUR;

function lerp(a, b, u) {
  return a + (b - a) * u;
}

function cfgNum(cfg, key, fallback) {
  var v = cfg && cfg[key];
  return v != null && isFinite(Number(v)) ? Number(v) : fallback;
}

function fallCountdownTickMs(fall, cfg) {
  var tickMs = fall && fall.countdownTickMs != null ? Number(fall.countdownTickMs) : NaN;
  if (!isFinite(tickMs) || tickMs <= 0) {
    tickMs = cfgNum(cfg, "FALL_COUNTDOWN_TICK_MS", 1000);
  }
  return Math.max(1, tickMs);
}

function cfgStr(cfg, key, fallback) {
  var v = cfg && cfg[key];
  return v != null ? String(v) : fallback;
}

/**
 * @param {object} [cfg] RUN_CONFIG slice
 * @returns {"auto"|"dynamic"|"static"}
 */
export function resolveSkyBackgroundPreference(cfg) {
  var mode = cfgStr(cfg, "SKY_BACKGROUND_MODE", SKY_BACKGROUND_MODES.AUTO);
  if (mode === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  if (mode === SKY_BACKGROUND_MODES.DYNAMIC) return SKY_BACKGROUND_MODES.DYNAMIC;
  return SKY_BACKGROUND_MODES.AUTO;
}

/**
 * Effective motion tier for hour mapping (static vs dynamic crawl).
 * @param {object} [cfg] RUN_CONFIG slice
 * @returns {"dynamic"|"static"}
 */
export function resolveSkyBackgroundMode(cfg) {
  var effective = cfgStr(cfg, "SKY_BACKGROUND_EFFECTIVE", "");
  if (effective === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  if (effective === SKY_BACKGROUND_MODES.DYNAMIC) return SKY_BACKGROUND_MODES.DYNAMIC;
  var pref = resolveSkyBackgroundPreference(cfg);
  if (pref === SKY_BACKGROUND_MODES.STATIC) return SKY_BACKGROUND_MODES.STATIC;
  return SKY_BACKGROUND_MODES.DYNAMIC;
}

/** @param {object} [cfg] */
export function isStaticSkyBackground(cfg) {
  return resolveSkyBackgroundMode(cfg) === SKY_BACKGROUND_MODES.STATIC;
}

function staticFallHoldHour(cfg) {
  return cfgNum(cfg, "STATIC_FALL_HOLD_HOUR", 5);
}

function staticShakeHoldHour(cfg) {
  return cfgNum(cfg, "STATIC_SHAKE_HOLD_HOUR", 19);
}

/**
 * Next 24 h boundary strictly after the current hour (24, 48, 72, …).
 * Hour never decreases — NEXT DAY timelapses forward to this anchor.
 * @param {number} hour
 */
export function nextCycleBoundaryHour(hour) {
  var h = Number(hour);
  if (!isFinite(h) || h < 0) h = 0;
  return (Math.floor(h / CYCLE_HOURS) + 1) * CYCLE_HOURS;
}

/**
 * Run-relative sky hour base from saved cycle index (checkpoint has no cycleBaseHour).
 * cycleIndex N → base N × 24 for both Fall and Merge resume (0 → dawn 0, 1 → 24, …).
 * @param {number} cycleIndex
 * @param {"fall"|"shake"} [_resumeStage]
 */
export function cycleBaseHourFromCycleIndex(cycleIndex, _resumeStage) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  return idx * CYCLE_HOURS;
}

/** Clamp a saved hour into the Merge crawl band for the current cycle base. */
export function clampShakeCrawlHour(ctrl, hour) {
  var cfg = ctrl && ctrl.cfg;
  var base = ctrl && ctrl.cycleBaseHour != null ? Number(ctrl.cycleBaseHour) : 0;
  if (!isFinite(base) || base < 0) base = 0;
  if (isStaticSkyBackground(cfg)) {
    return base + staticShakeHoldHour(cfg);
  }
  var start = base + SHAKE_CRAWL_START_HOUR;
  var target = base + SHAKE_CRAWL_TARGET_HOUR;
  var h = Number(hour);
  if (!isFinite(h)) return start;
  if (h < start) return start;
  if (h > target) return target;
  return h;
}

/**
 * Resume Merge crawl at a saved hour (checkpoint reload) instead of re-anchoring at 16.
 * @param {ReturnType<typeof createSkyClockController>} ctrl
 * @param {number} hour
 * @param {number} nowMs
 */
export function restoreShakeCrawlFromHour(ctrl, hour, nowMs) {
  var cfg = ctrl.cfg;
  var wall = Number(nowMs) || 0;
  ctrl.mode = SKY_MODE.SHAKE_CRAWL;
  ctrl.shakeCrawlStartMs = wall;
  ctrl.frozenHour = null;
  ctrl.pauseOffsetMs = 0;
  ctrl.pausedAtMs = null;
  if (isStaticSkyBackground(cfg)) {
    return ctrl;
  }
  var base = ctrl.cycleBaseHour || 0;
  var target = base + cfgNum(cfg, "SHAKE_SKY_CRAWL_TARGET_HOUR", SHAKE_CRAWL_TARGET_HOUR);
  var start = base + cfgNum(cfg, "SHAKE_SKY_CRAWL_START_HOUR", SHAKE_CRAWL_START_HOUR);
  var span = target - start;
  var clamped = clampShakeCrawlHour(ctrl, hour);
  var tauSec = crawlTauMs(cfg) / 1000;
  var ratio = span > 0 ? (target - clamped) / span : 0;
  var elapsedSec = 0;
  if (ratio > 0.0001 && ratio < 0.9999) {
    elapsedSec = -tauSec * Math.log(ratio);
  } else if (ratio <= 0.0001) {
    elapsedSec = tauSec * 6;
  }
  ctrl.shakeCrawlStartMs = wall - Math.max(0, elapsedSec) * 1000;
  return ctrl;
}

/**
 * @param {object} cfg RUN_CONFIG slice
 */
export function createSkyClockController(cfg) {
  return {
    cfg: cfg || {},
    mode: SKY_MODE.FALL,
    cycleBaseHour: 0,
    transitionStartMs: 0,
    transitionFromHour: FALL_END_HOUR,
    transitionToHour: SHAKE_CRAWL_START_HOUR,
    shakeCrawlStartMs: 0,
    pauseOffsetMs: 0,
    pausedAtMs: null,
    frozenHour: null,
  };
}

export function resetSkyClockController(ctrl) {
  var next = createSkyClockController(ctrl.cfg);
  Object.assign(ctrl, next);
  return ctrl;
}

export function beginFallSkyClock(ctrl) {
  ctrl.mode = SKY_MODE.FALL;
  ctrl.frozenHour = null;
  ctrl.pauseOffsetMs = 0;
  ctrl.pausedAtMs = null;
  return ctrl;
}

export function beginTransitionToShake(ctrl, nowMs, fromHour) {
  var base = ctrl.cycleBaseHour || 0;
  var cfg = ctrl.cfg;
  ctrl.mode = SKY_MODE.TRANSITION_TO_SHAKE;
  ctrl.transitionStartMs = nowMs;
  if (isStaticSkyBackground(cfg)) {
    ctrl.transitionFromHour = base + staticFallHoldHour(cfg);
    ctrl.transitionToHour = base + staticShakeHoldHour(cfg);
  } else {
    var toHour = base + cfgNum(cfg, "SHAKE_SKY_CRAWL_START_HOUR", SHAKE_CRAWL_START_HOUR);
    var defaultFrom = base + FALL_END_HOUR;
    var resolvedFrom = (fromHour != null && isFinite(fromHour)) ? fromHour : defaultFrom;
    if (resolvedFrom < base) resolvedFrom = base;
    if (resolvedFrom > toHour) resolvedFrom = toHour;
    ctrl.transitionFromHour = resolvedFrom;
    ctrl.transitionToHour = toHour;
  }
  ctrl.frozenHour = null;
  return ctrl;
}

export function completeTransitionToShake(ctrl, nowMs) {
  ctrl.mode = SKY_MODE.SHAKE_CRAWL;
  ctrl.shakeCrawlStartMs = nowMs;
  ctrl.frozenHour = null;
  return ctrl;
}

/**
 * @param {ReturnType<typeof createSkyClockController>} ctrl
 * @param {number} fromHour
 * @param {number} nowMs
 */
export function beginTransitionToFall(ctrl, fromHour, nowMs) {
  var cfg = ctrl.cfg;
  var from = isFinite(fromHour) ? fromHour : (ctrl.cycleBaseHour || 0) + SHAKE_CRAWL_START_HOUR;
  ctrl.mode = SKY_MODE.TRANSITION_TO_FALL;
  ctrl.transitionStartMs = nowMs;
  ctrl.transitionFromHour = from;
  if (isStaticSkyBackground(cfg)) {
    ctrl.transitionToHour = nextCycleBoundaryHour(from) + staticFallHoldHour(cfg);
  } else {
    ctrl.transitionToHour = nextCycleBoundaryHour(from);
  }
  ctrl.frozenHour = null;
  return ctrl;
}

export function completeTransitionToFall(ctrl) {
  var cfg = ctrl.cfg;
  if (isStaticSkyBackground(cfg)) {
    ctrl.cycleBaseHour = nextCycleBoundaryHour(ctrl.transitionFromHour);
  } else if (ctrl.transitionToHour != null && isFinite(ctrl.transitionToHour)) {
    ctrl.cycleBaseHour = ctrl.transitionToHour;
  } else {
    ctrl.cycleBaseHour = ctrl.cycleBaseHour || 0;
  }
  return beginFallSkyClock(ctrl);
}

export function freezeSkyClock(ctrl, hour, nowMs) {
  if (ctrl.pausedAtMs != null) return ctrl;
  ctrl.pausedAtMs = nowMs;
  ctrl.frozenHour = hour;
  return ctrl;
}

export function unfreezeSkyClock(ctrl, nowMs) {
  if (ctrl.pausedAtMs == null) return ctrl;
  ctrl.pauseOffsetMs += Math.max(0, nowMs - ctrl.pausedAtMs);
  ctrl.pausedAtMs = null;
  ctrl.frozenHour = null;
  return ctrl;
}

/** Wall time mapped through accumulated bridge/gameplay pause offsets. */
export function getSkyClockEffectiveNowMs(ctrl, wallNowMs) {
  return effectiveNow(ctrl, wallNowMs);
}

/** Clear pause offsets when starting a new bridge segment or after handoff. */
export function resetSkyClockPauseMotion(ctrl, nowMs) {
  if (!ctrl) return ctrl;
  unfreezeSkyClock(ctrl, nowMs);
  ctrl.pauseOffsetMs = 0;
  return ctrl;
}

function effectiveNow(ctrl, nowMs) {
  var t = Number(nowMs) || 0;
  var offset = ctrl.pauseOffsetMs || 0;
  if (ctrl.pausedAtMs != null) return ctrl.pausedAtMs - offset;
  return t - offset;
}

function transitionDurationMs(cfg) {
  return Math.max(1, cfgNum(cfg, "DAY_NIGHT_TRANSITION_MS", 2000));
}

function crawlTauMs(cfg) {
  return Math.max(1000, cfgNum(cfg, "SHAKE_SKY_CRAWL_TAU_MS", 75000));
}

function hourFromTransition(ctrl, nowMs) {
  var cfg = ctrl.cfg;
  var dur = transitionDurationMs(cfg);
  var t0 = ctrl.transitionStartMs;
  var elapsed = Math.max(0, effectiveNow(ctrl, nowMs) - t0);
  var u = easeInOut(Math.min(1, elapsed / dur));
  return lerp(ctrl.transitionFromHour, ctrl.transitionToHour, u);
}

function hourFromShakeCrawl(ctrl, nowMs) {
  var cfg = ctrl.cfg;
  var base = ctrl.cycleBaseHour || 0;
  if (isStaticSkyBackground(cfg)) {
    return base + staticShakeHoldHour(cfg);
  }
  var target = base + cfgNum(cfg, "SHAKE_SKY_CRAWL_TARGET_HOUR", SHAKE_CRAWL_TARGET_HOUR);
  var start = base + cfgNum(cfg, "SHAKE_SKY_CRAWL_START_HOUR", SHAKE_CRAWL_START_HOUR);
  var span = target - start;
  var t0 = ctrl.shakeCrawlStartMs;
  var elapsedSec = Math.max(0, effectiveNow(ctrl, nowMs) - t0) / 1000;
  var tauSec = crawlTauMs(cfg) / 1000;
  var hour = target - span * Math.exp(-elapsedSec / tauSec);
  if (hour >= target - 0.02) return target;
  return hour;
}

function hourFromFallPlaySettleElapsedMs(fall, cfg) {
  fall = fall || {};
  cfg = cfg || {};
  var runMs = Math.max(0, Number(fall.runDurationSec) || cfgNum(cfg, "RUN_DURATION", 20)) * 1000;
  var settleTotalMs = Math.max(0, Number(fall.settleTotalMs) || cfgNum(cfg, "FALL_SETTLE_MS", 2000));
  if (fall.state === "settle" || fall.isSettling) {
    var settleRem = Math.max(0, Number(fall.settleRemainingMs) || 0);
    var settleElapsed = Math.max(0, settleTotalMs - settleRem);
    return runMs + settleElapsed;
  }
  var runRem = Math.max(0, Number(fall.timeRemainingExact) || 0);
  var runElapsed = Math.max(0, runMs - runRem * 1000);
  return runElapsed;
}

function hourFromFall(ctrl, snapshot) {
  var cfg = ctrl.cfg;
  var base = ctrl.cycleBaseHour || 0;
  if (isStaticSkyBackground(cfg)) {
    return base + staticFallHoldHour(cfg);
  }
  var fall = snapshot.fall;
  if (!fall) return base;
  var holdHour = staticFallHoldHour(cfg);

  if (snapshot.skyAutoProbeCountdown) {
    var tickMs = fallCountdownTickMs(fall, cfg);
    var countdownTotalMs = Math.max(1, Number(fall.countdownTotalSec) || 0) * tickMs;
    var remCd = Math.max(0, Number(fall.countdownRemaining) || 0);
    var cdElapsed = Math.max(0, countdownTotalMs - remCd * tickMs);
    return base + holdHour * clamp01(cdElapsed / countdownTotalMs);
  }

  if (snapshot.skyUseFirstFallSplitHourArc) {
    var runMs = Math.max(1, Number(fall.runDurationSec) || cfgNum(cfg, "RUN_DURATION", 20)) * 1000;
    var settleTotalMs = Math.max(0, Number(fall.settleTotalMs) || cfgNum(cfg, "FALL_SETTLE_MS", 2000));
    var playSettleTotal = runMs + settleTotalMs;
    var playSettleElapsed = hourFromFallPlaySettleElapsedMs(fall, cfg);
    var span = FALL_END_HOUR - holdHour;
    return base + holdHour + span * clamp01(playSettleElapsed / playSettleTotal);
  }

  var total = fallSkyTotalMs(cfg, !!snapshot.isFirstFallOfFirstRun, fall);
  var elapsed = computeFallSkyElapsedMs(fall, cfg);
  return base + FALL_END_HOUR * clamp01(elapsed / total);
}

/**
 * Fall wall-time totals for hour arc (countdown + play + settle).
 * @param {object} cfg
 * @param {boolean} [_isFirstFallOfFirstRun] Unused; kept for call-site compatibility.
 * @param {object} [fall]
 */
export function fallSkyTotalMs(cfg, _isFirstFallOfFirstRun, fall) {
  fall = fall || {};
  var countdownSec = Number(fall.countdownTotalSec);
  // Honor explicit 0 (later Falls skip countdown). Fallback only when missing.
  if (!isFinite(countdownSec) || countdownSec < 0) {
    countdownSec = cfgNum(cfg, "RUN_COUNTDOWN_SECONDS", 3);
  }
  var runSec = Number(fall.runDurationSec);
  if (!isFinite(runSec) || runSec <= 0) {
    runSec = cfgNum(cfg, "RUN_DURATION", 20);
  }
  var settleMs = Number(fall.settleTotalMs);
  if (!isFinite(settleMs) || settleMs < 0) {
    settleMs = cfgNum(cfg, "FALL_SETTLE_MS", 2000);
  }
  var tickMs = fallCountdownTickMs(fall, cfg);
  return Math.max(1, countdownSec * tickMs + runSec * 1000 + settleMs);
}

/**
 * @param {{
 *   state?: string,
 *   countdownRemaining?: number,
 *   countdownTotalSec?: number,
 *   countdownTickMs?: number,
 *   timeRemainingExact?: number,
 *   runDurationSec?: number,
 *   settleRemainingMs?: number,
 *   settleTotalMs?: number,
 *   isFirstFallOfFirstRun?: boolean,
 * }} fall
 */
export function computeFallSkyElapsedMs(fall, cfg) {
  fall = fall || {};
  cfg = cfg || {};
  var tickMs = fallCountdownTickMs(fall, cfg);
  var countdownTotalMs = Math.max(0, Number(fall.countdownTotalSec) || 0) * tickMs;
  var runMs = Math.max(0, Number(fall.runDurationSec) || cfgNum(cfg, "RUN_DURATION", 20)) * 1000;
  var settleTotalMs = Math.max(0, Number(fall.settleTotalMs) || cfgNum(cfg, "FALL_SETTLE_MS", 2000));

  if (fall.state === "countdown") {
    var remCd = Math.max(0, Number(fall.countdownRemaining) || 0);
    return countdownTotalMs - remCd * tickMs;
  }

  var afterCountdown = countdownTotalMs;

  if (fall.state === "settle" || fall.isSettling) {
    var settleRem = Math.max(0, Number(fall.settleRemainingMs) || 0);
    var settleElapsed = Math.max(0, settleTotalMs - settleRem);
    return afterCountdown + runMs + settleElapsed;
  }

  var runRem = Math.max(0, Number(fall.timeRemainingExact) || 0);
  var runElapsed = Math.max(0, runMs - runRem * 1000);
  return afterCountdown + runElapsed;
}

/**
 * Progress bar shrink 0..1 (after countdown only; active play only — not settle).
 * @param {Parameters<typeof computeFallSkyElapsedMs>[0]} fall
 * @param {object} cfg
 */
export function computeFallBarShrink(fall, cfg) {
  fall = fall || {};
  if (fall.state === "countdown") return 0;
  if (fall.state === "settle" || fall.isSettling) return 1;
  cfg = cfg || {};
  var runMs = Math.max(1, Number(fall.runDurationSec) || cfgNum(cfg, "RUN_DURATION", 20)) * 1000;
  var runRem = Math.max(0, Number(fall.timeRemainingExact) || 0);
  var runElapsed = Math.max(0, runMs - runRem * 1000);
  return clamp01(runElapsed / runMs);
}

/**
 * @param {ReturnType<typeof createSkyClockController>} ctrl
 * @param {{
 *   nowMs: number,
 *   frozenHour?: number|null,
 *   fall?: object,
 *   isFirstFallOfFirstRun?: boolean,
 *   skyAutoProbeCountdown?: boolean,
 *   skyUseFirstFallSplitHourArc?: boolean,
 * }} snapshot
 */
export function getSkyHour(ctrl, snapshot) {
  snapshot = snapshot || {};
  if (snapshot.frozenHour != null && isFinite(snapshot.frozenHour)) {
    return snapshot.frozenHour;
  }
  if (ctrl.frozenHour != null && isFinite(ctrl.frozenHour)) {
    return ctrl.frozenHour;
  }

  var nowMs = snapshot.nowMs != null ? snapshot.nowMs : 0;

  if (ctrl.mode === SKY_MODE.TRANSITION_TO_SHAKE || ctrl.mode === SKY_MODE.TRANSITION_TO_FALL) {
    return hourFromTransition(ctrl, nowMs);
  }

  if (ctrl.mode === SKY_MODE.SHAKE_CRAWL) {
    return hourFromShakeCrawl(ctrl, nowMs);
  }

  return hourFromFall(ctrl, snapshot);
}

export function getSkyHourForFallProgress(ctrl, snapshot) {
  return getSkyHour(ctrl, snapshot);
}